import {
  AuthLevelCorporation,
  AuthLevelIndividual,
  CloudGovFormSchema,
  SmartApplyFormSchemaValidator,
  TaxRateLiterals,
} from "../..";
import {
  FormSchema,
  IFormSchemaV2,
  ITextInputElement,
} from "@graffer-inc/form-schema";
import { CloudGovFormSchemaAccessor } from "./form-schema/smart-apply-form-schema-validator/accessor-helper";
import { InvoiceItemValidator } from "./invoice-item-validator";
import { ApplicantSchemaKeys } from "./form-schema/applicant-schema-keys";
import { SmartApplyConditionalRuleValidator } from "./form-schema/smart-apply-form-schema-validator/smart-apply-conditional-rule-validator";

export const MAX_PAYMENT_ITEM_NUM = 5;

export class ProcedureDetail {
  template: Template;
  acceptAnyFile: boolean;
  paymentSetting?: PaymentSettingV2;
  requireAuthLevel: {
    corporation: AuthLevelCorporation;
    individual: AuthLevelIndividual;
  };
  applicantSchemaKeys: ApplicantSchemaKeys;

  constructor(values: ProcedureDetailType) {
    this.template = values.template;
    this.acceptAnyFile = values.acceptAnyFile;
    this.paymentSetting = values.paymentSetting;
    this.requireAuthLevel = values.requireAuthLevel;
    this.applicantSchemaKeys = values.applicantSchemaKeys;
  }

  isValid() {
    return (
      this.formSchemaValidator().isValid() &&
      this.isSignatureAcceptable() &&
      this.isPaymentSettingValid()
    );
  }

  private isSignatureAcceptable(): boolean {
    if (this.requireAuthLevel.individual === "MY_NUMBER_CARD_SIGNATURE") {
      return (
        this.isIndividualOnly() &&
        typeof new SmartApplyConditionalRuleValidator(
          this.template.formSchema,
          this.applicantSchemaKeys,
        ).myNumberSignatureRequiredElements() === "undefined"
      );
    }

    if (this.requireAuthLevel.corporation === "MOJ_SIGNATURE") {
      return (
        this.isCorporateOnly() &&
        typeof new SmartApplyConditionalRuleValidator(
          this.template.formSchema,
          this.applicantSchemaKeys,
        ).mojSignatureRequiredElements() === "undefined"
      );
    }

    return true;
  }

  private isPaymentSettingValid() {
    if (!this.paymentSetting) {
      return true;
    }
    if (!this.paymentSetting.items) {
      return false;
    }
    const formulaIDs = this.formulaIds();
    for (let item of this.paymentSetting.items) {
      if (formulaIDs.indexOf(item.formulaId) === -1) {
        return false;
      }
      if (!new InvoiceItemValidator(item).isValid()) {
        return false;
      }
    }
    if (this.paymentSetting.items.length > MAX_PAYMENT_ITEM_NUM) {
      return false;
    }
    return true;
  }

  isIndividualOnly() {
    const supported = this.schemaAccessor().supportedApplicantTypes;
    return supported.length === 1 && supported[0] === "INDIVIDUAL";
  }

  isCorporateOnly() {
    const supported = this.schemaAccessor().supportedApplicantTypes;
    return supported.length === 1 && supported[0] === "CORPORATE";
  }

  private formulaIds() {
    return this.cloudGovFormSchema()
      .getFormulas()
      .map((v) => v.id);
  }

  private formSchemaValidator() {
    return new SmartApplyFormSchemaValidator(
      this.template.formSchema,
      this.applicantSchemaKeys,
    );
  }

  private schemaAccessor() {
    return new CloudGovFormSchemaAccessor(
      this.template.formSchema,
      this.applicantSchemaKeys,
    );
  }

  private cloudGovFormSchema() {
    return CloudGovFormSchema.fromJSON(this.template.formSchema);
  }
}

export type PublishSetting = {
  startAcceptAt?: Date;
  endAcceptAt?: Date;
  publishAt?: Date;
  unPublishAt?: Date;
  isPausingAccept: boolean;
};

export type PaymentSettingV2 = {
  items?: InvoiceItemSetting[];
};

export type InvoiceItemSetting = {
  formulaId: string;
  taxRate: TaxRateLiterals;
  description: string;
};

export type ProcedureDetailType = {
  template: Template;
  acceptAnyFile: boolean;
  paymentSetting?: PaymentSettingV2;
  requireAuthLevel: {
    corporation: AuthLevelCorporation;
    individual: AuthLevelIndividual;
  };
  applicantSchemaKeys: ApplicantSchemaKeys;
};

export type Template = {
  formSchema: IFormSchemaV2;
  tenantProcessResultSetting?: TenantProcessResultSetting;
  pdfOutputSetting?: PDFOutputSetting;
};

export type TenantProcessResultSetting = {
  inputElements: ITextInputElement[];
};

export type PDFOutputSetting = {
  pdfOutputBaseFile?: any;
  pdfOutputBaseFileURL?: string;
  pdfOutputBaseFileName?: string;
  metaInfoFormSchema?: FormSchema;
};

export * from "./form-schema/applicant-schema-keys";
